import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { ID8_GEN_TYPES, ID8_TABS, PROMPT_TYPE } from '../../constants';
import updateReactionByImageId from '../../utils/updateReactionByImageId';
import {
  consumerCustomImage,
  consumerLikeId8Image,
  consumerModifyConcept,
  consumerRunAgain,
  deleteMessageConsumer,
  getConsumerId8Data,
  getConsumerImages,
  getIdeasMessagesConsumer,
  getMessageLogsConsumer,
  resetSessionConsumer,
  selectMessageConsumer,
  consumerGetPromptById,
  getConsumerAdImages,
  recreateAdConsumer,
  runAgainAdConsumer,
  modifyAdConceptConsumer,
} from '../consumer/id8/consumerId8Actions';
import {
  clearAll,
  deleteMessage,
  deleteMultipleMessages,
  generateMessages,
  getBackupMessages,
  getId8FormData,
  getIdeasMessages,
  getImages,
  getMessageLogs,
  getPromptById,
  likeId8Image,
  reclaimBackup,
  replyId8,
  resetSession,
  restoreMessage,
  selectMessage,
  getAgents,
  getProjectAgent,
  getAdImages,
  recreateAd,
  runAgainAd,
  modifyAdConcept,
  runAgain,
  modifyConcept,
  customImage,
} from './id8Actions';

const DEFAULT_TABLE_SETTINGS = {
  searchQuery: '',
  page: 1,
  sort: '-createdOn',
  size: 8,
  totalElements: 0,
};

const id8InitialState = {
  generationForm: {
    byPass: '',
    focusOn: '',
    url: '',
    fileText: '',
    hint: '',
  },

  projectName: '',
  projectId: '',

  isFormLoading: false,
  isResetting: false,

  id8Tab: ID8_TABS.IDEAS,

  isDirty: false,
  editableMessage: {
    id: null,
    message: '',
  },

  messagesContent: [],
  isMessagesLoading: false,

  messageLogs: {
    histories: [],
    original: {},
    imagesPromptCount: 0,
  },
  isMessageLogsLoading: false,

  tableSettings: {
    sort: '-count',
    searchQuery: '',
  },

  imagesContent: [],
  isImagesLoading: false,
  imagesTableSettings: DEFAULT_TABLE_SETTINGS,
  generatingMessageId: '',

  likedImagesContent: [],
  isLikedImagesLoading: false,
  likedImagesTableSettings: DEFAULT_TABLE_SETTINGS,

  adImagesContent: [],
  isAdImagesLoading: false,
  adImagesTableSettings: DEFAULT_TABLE_SETTINGS,

  loadingActions: {},

  agentsList: [],
  isAgentsLoading: false,
  projectAgent: null,

  activeGeneratingStatuses: {
    [ID8_GEN_TYPES.IDEAS]: false,
    [ID8_GEN_TYPES.IMAGES]: false,
    [ID8_GEN_TYPES.RECREATE]: false,
    [ID8_GEN_TYPES.LIKED]: false,
  },
};

function changeGeneratingStatusHelper(state, payload) {
  const { genType, status } = payload;
  state.activeGeneratingStatuses[genType] = status;
}

const id8Slice = createSlice({
  name: 'id8',
  initialState: id8InitialState,
  reducers: {
    changeId8Tab: (state, action) => {
      state.id8Tab = action.payload;

      state.messagesContent = [];
    },

    changeLocalMessage: (state, action) => {
      state.messagesContent = state.messagesContent.map((message) =>
        message.id === action.payload.id ? { ...message, message: action.payload.message } : { ...message },
      );
    },

    setIsDirty: (state, action) => {
      state.isDirty = action.payload;
    },
    setEditableMessage: (state, action) => {
      state.editableMessage = action.payload;
    },
    changeEditableMessage: (state, action) => {
      state.editableMessage.message = action.payload;
    },

    likeLocalMessage: (state, action) => {
      state.messagesContent = state.messagesContent.map((message) =>
        message.id === action.payload.id ? { ...message, reaction: action.payload.reaction } : { ...message },
      );
    },

    changeTableSort: (state, action) => {
      state.tableSettings.sort = action.payload;
      state.tableSettings.page = 0;
    },
    changeTableSearch: (state, action) => {
      state.tableSettings.searchQuery = action.payload;
    },

    changeGeneratingStatus: (state, action) => {
      //deprecated
      state.isGenerating = action.payload;
    },

    resetId8MessageLogs: (state) => {
      state.messageLogs = id8InitialState.messageLogs;
    },

    changeImagesTable: (state, action) => {
      state.imagesTableSettings = { ...state.imagesTableSettings, ...action.payload };
    },
    changeLikedImagesTable: (state, action) => {
      state.likedImagesTableSettings = { ...state.likedImagesTableSettings, ...action.payload };
    },
    changeAdImagesTable: (state, action) => {
      state.adImagesTableSettings = { ...state.adImagesTableSettings, ...action.payload };
    },

    changeActiveGenStatus: (state, action) => {
      changeGeneratingStatusHelper(state, action.payload);
    },

    shiftPromptToImages: (state, action) => {
      const { prompt, promptType } = action.payload;

      if (promptType === PROMPT_TYPE.IDEA) {
        if (state.imagesContent.length >= state.imagesTableSettings.size) {
          const imagesWithoutLast = state.imagesContent.slice(0, -1);
          state.imagesContent = [prompt, ...imagesWithoutLast];
        } else {
          state.imagesContent = [prompt, ...state.imagesContent];
        }
      }
      if (promptType === PROMPT_TYPE.RECREATE) {
        if (state.adImagesContent.length >= state.adImagesTableSettings.size) {
          const imagesWithoutLast = state.adImagesContent.slice(0, -1);
          state.adImagesContent = [prompt, ...imagesWithoutLast];
        } else {
          state.adImagesContent = [prompt, ...state.adImagesContent];
        }
      }
    },
    changeGeneratingStatusForPrompt: (state, action) => {
      const { id, generating, promptType } = action.payload;
      if (promptType === PROMPT_TYPE.IDEA) {
        state.imagesContent = state.imagesContent.map((image) =>
          image.id === id ? { ...image, generating } : { ...image },
        );
      }
      if (promptType === PROMPT_TYPE.RECREATE) {
        state.adImagesContent = state.adImagesContent.map((image) =>
          image.id === id ? { ...image, generating } : { ...image },
        );
      }
    },

    resetId8Agents: (state) => {
      state.agentsList = [];
      state.projectAgent = null;
    },
    resetId8Params: (state) => {
      state.generationForm = id8InitialState.generationForm;

      state.projectName = id8InitialState.projectName;
      state.projectId = id8InitialState.projectId;

      state.isFormLoading = id8InitialState.isFormLoading;
      state.isResetting = id8InitialState.isResetting;

      state.id8Tab = id8InitialState.id8Tab;

      state.messagesContent = id8InitialState.messagesContent;
      state.isMessagesLoading = id8InitialState.isMessagesLoading;
      state.messageLogs = id8InitialState.messageLogs;
      state.isMessageLogsLoading = id8InitialState.isMessageLogsLoading;
      state.tableSettings = id8InitialState.tableSettings;

      state.imagesContent = id8InitialState.imagesContent;
      state.isImagesLoading = id8InitialState.isImagesLoading;
      state.imagesTableSettings = id8InitialState.imagesTableSettings;
      state.generatingMessageId = id8InitialState.generatingMessageId;

      state.likedImagesContent = id8InitialState.likedImagesContent;
      state.isLikedImagesLoading = id8InitialState.isLikedImagesLoading;
      state.likedImagesTableSettings = id8InitialState.likedImagesTableSettings;

      state.adImagesContent = id8InitialState.adImagesContent;
      state.isAdImagesLoading = id8InitialState.isAdImagesLoading;
      state.adImagesTableSettings = id8InitialState.adImagesTableSettings;

      state.activeGeneratingStatuses = id8InitialState.activeGeneratingStatuses;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(reclaimBackup.fulfilled, (state) => {
        state.isFormLoading = false;
      })

      .addCase(clearAll.fulfilled, (state) => {
        state.messagesContent = [];
      })

      //GET_AGENTS
      .addCase(getAgents.fulfilled, (state, action) => {
        state.agentsList = action.payload;
      })

      //GET_PROJECT_AGENT
      .addCase(getProjectAgent.fulfilled, (state, action) => {
        state.projectAgent = action.payload;
      })

      .addMatcher(
        isAnyOf(
          getId8FormData.fulfilled,
          generateMessages.fulfilled,
          resetSession.fulfilled,
          replyId8.fulfilled,
          clearAll.fulfilled,

          getConsumerId8Data.fulfilled,
          resetSessionConsumer.fulfilled,
        ),
        (state, action) => {
          state.isFormLoading = false;

          state.generationForm.byPass = action.payload.byPass || '';
          state.generationForm.focusOn = action.payload.focusOn || '';
          state.generationForm.url = action.payload.url || '';
          state.generationForm.hint = action.payload.hint || '';
          state.projectName = action.payload.projectName;
          state.projectId = action.payload.id;
        },
      )
      .addMatcher(
        isAnyOf(
          getId8FormData.pending,
          resetSession.pending,
          clearAll.pending,
          reclaimBackup.pending,

          getConsumerId8Data.pending,
          resetSessionConsumer.pending,
        ),
        (state) => {
          state.isFormLoading = true;
        },
      )
      .addMatcher(
        isAnyOf(
          getId8FormData.rejected,
          resetSession.rejected,
          clearAll.rejected,
          reclaimBackup.rejected,

          getConsumerId8Data.rejected,
          resetSessionConsumer.rejected,
        ),
        (state) => {
          state.isFormLoading = false;
        },
      )

      //RESET SESSION
      .addMatcher(isAnyOf(resetSession.fulfilled, resetSessionConsumer.fulfilled), (state) => {
        state.isResetting = false;
      })
      .addMatcher(isAnyOf(resetSession.pending, resetSessionConsumer.pending), (state) => {
        state.isResetting = true;
      })
      .addMatcher(isAnyOf(resetSession.rejected, resetSessionConsumer.rejected), (state) => {
        state.isResetting = false;
      })

      // MESSAGES LOADING
      .addMatcher(
        isAnyOf(
          getIdeasMessages.pending,
          getBackupMessages.pending,
          deleteMessage.pending,

          getIdeasMessagesConsumer.pending,
          deleteMessageConsumer.pending,
        ),
        (state) => {
          state.isMessagesLoading = true;
        },
      )
      .addMatcher(
        isAnyOf(
          getIdeasMessages.rejected,
          getBackupMessages.rejected,
          deleteMessage.rejected,

          getIdeasMessagesConsumer.rejected,
          deleteMessageConsumer.rejected,
        ),
        (state) => {
          state.isMessagesLoading = false;
        },
      )
      .addMatcher(
        isAnyOf(
          getIdeasMessages.fulfilled,
          getBackupMessages.fulfilled,

          getIdeasMessagesConsumer.fulfilled,
        ),
        (state, action) => {
          state.isMessagesLoading = false;
          state.messagesContent = action.payload;
        },
      )

      //SELECT MESSAGE
      .addMatcher(isAnyOf(selectMessage.fulfilled, selectMessageConsumer.fulfilled), (state, action) => {
        state.messagesContent = state.messagesContent.map((message) =>
          message.id === action.payload.id ? { ...message, selected: action.payload.selected } : { ...message },
        );
        changeGeneratingStatusHelper(state, { genType: ID8_GEN_TYPES.IMAGES, status: false });
        state.generatingMessageId = '';
      })
      .addMatcher(isAnyOf(selectMessage.pending, selectMessageConsumer.pending), (state, action) => {
        changeGeneratingStatusHelper(state, { genType: ID8_GEN_TYPES.IMAGES, status: true });
        state.generatingMessageId = action.meta.arg.id;
      })
      .addMatcher(isAnyOf(selectMessage.rejected, selectMessageConsumer.rejected), (state) => {
        changeGeneratingStatusHelper(state, { genType: ID8_GEN_TYPES.IMAGES, status: false });
        state.generatingMessageId = '';
      })

      //DELETE MESSAGE
      .addMatcher(
        isAnyOf(deleteMessage.fulfilled, restoreMessage.fulfilled, deleteMessageConsumer.fulfilled),
        (state, action) => {
          state.isMessagesLoading = false;
          state.messagesContent = state.messagesContent.filter((msg) => msg.id !== action.payload);
        },
      )

      //DELETE MESSAGES
      .addMatcher(isAnyOf(deleteMultipleMessages.fulfilled), (state) => {
        state.isMessagesLoading = false;
      })
      .addMatcher(isAnyOf(deleteMultipleMessages.pending), (state) => {
        state.isMessagesLoading = true;
      })
      .addMatcher(isAnyOf(deleteMultipleMessages.rejected), (state) => {
        state.isMessagesLoading = false;
      })

      //MESSAGE LOGS
      .addMatcher(isAnyOf(getMessageLogs.fulfilled, getMessageLogsConsumer.fulfilled), (state, action) => {
        state.messageLogs.histories = action.payload.histories;
        state.messageLogs.original = action.payload.original;
        state.messageLogs.imagesPromptCount = action.payload.imagesPromptCount;
        state.isMessageLogsLoading = false;
      })
      .addMatcher(isAnyOf(getMessageLogs.pending, getMessageLogsConsumer.pending), (state) => {
        state.isMessageLogsLoading = true;
      })
      .addMatcher(isAnyOf(getMessageLogs.rejected, getMessageLogsConsumer.rejected), (state) => {
        state.isMessageLogsLoading = false;
      })

      //GET _IMAGES
      .addMatcher(isAnyOf(getImages.fulfilled, getConsumerImages.fulfilled), (state, action) => {
        state.isImagesLoading = false;
        state.imagesTableSettings.totalElements = action.payload.totalElements;
        state.imagesContent = action.payload.content;
      })
      .addMatcher(isAnyOf(getImages.pending, getConsumerImages.pending), (state) => {
        state.isImagesLoading = true;
      })
      .addMatcher(isAnyOf(getImages.rejected, getConsumerImages.rejected), (state) => {
        state.isImagesLoading = false;
      })

      //GET_AD_IMAGES
      .addMatcher(isAnyOf(getAdImages.fulfilled, getConsumerAdImages.fulfilled), (state, action) => {
        state.isAdImagesLoading = false;
        state.adImagesTableSettings.totalElements = action.payload.totalElements;
        state.adImagesContent = action.payload.content;
      })
      .addMatcher(isAnyOf(getAdImages.pending, getConsumerAdImages.pending), (state) => {
        state.isAdImagesLoading = true;
      })
      .addMatcher(isAnyOf(getAdImages.rejected, getConsumerAdImages.rejected), (state) => {
        state.isAdImagesLoading = false;
      })

      //LIKE_IMAGE
      .addMatcher(isAnyOf(likeId8Image.fulfilled, consumerLikeId8Image.fulfilled), (state, action) => {
        const { promptType, imageId, newReaction, updatedOn } = action.payload;

        if (promptType === PROMPT_TYPE.IDEA) {
          state.imagesContent = updateReactionByImageId(state.imagesContent, imageId, newReaction, updatedOn);
        } else if (promptType === PROMPT_TYPE.RECREATE) {
          state.adImagesContent = updateReactionByImageId(state.adImagesContent, imageId, newReaction, updatedOn);
        }
      })

      .addMatcher(isAnyOf(getPromptById.fulfilled, consumerGetPromptById.fulfilled), (state, action) => {
        const { prompt, promptType } = action.payload;

        if (promptType === PROMPT_TYPE.IDEA) {
          state.imagesContent = state.imagesContent.map((image) =>
            image.id === prompt.id ? { ...image, ...prompt } : { ...image },
          );
        }
        if (promptType === PROMPT_TYPE.RECREATE) {
          state.adImagesContent = state.adImagesContent.map((image) =>
            image.id === prompt.id ? { ...image, ...prompt } : { ...image },
          );
        }
      })
      .addMatcher(isAnyOf(getPromptById.pending, consumerGetPromptById.pending), (state, action) => {
        const { promptId, promptType } = action.meta.arg;
        if (promptType === PROMPT_TYPE.IDEA) {
          state.imagesContent = state.imagesContent.map((image) =>
            image.id === promptId ? { ...image, generating: true } : { ...image },
          );
        }
        if (promptType === PROMPT_TYPE.RECREATE) {
          state.adImagesContent = state.adImagesContent.map((image) =>
            image.id === promptId ? { ...image, generating: true } : { ...image },
          );
        }
      })
      .addMatcher(isAnyOf(getPromptById.rejected, consumerGetPromptById.rejected), (state, action) => {
        const { promptId, promptType } = action.meta.arg;
        if (promptType === PROMPT_TYPE.IDEA) {
          state.imagesContent = state.imagesContent.map((image) =>
            image.id === promptId ? { ...image, generating: false } : { ...image },
          );
        } else if (promptType === PROMPT_TYPE.RECREATE) {
          state.adImagesContent = state.adImagesContent.map((image) =>
            image.id === promptId ? { ...image, generating: false } : { ...image },
          );
        }
      })

      //AD_LOADERS
      .addMatcher(
        isAnyOf(
          runAgainAd.fulfilled,
          modifyAdConcept.fulfilled,
          recreateAd.fulfilled,

          recreateAdConsumer.fulfilled,
          runAgainAdConsumer.fulfilled,
          modifyAdConceptConsumer.fulfilled,
        ),
        (state) => {
          state.isAdImagesLoading = false;
          changeGeneratingStatusHelper(state, { genType: ID8_GEN_TYPES.AD, status: false });
        },
      )
      .addMatcher(
        isAnyOf(
          runAgainAd.pending,
          modifyAdConcept.pending,
          recreateAd.pending,

          recreateAdConsumer.pending,
          runAgainAdConsumer.pending,
          modifyAdConceptConsumer.pending,
        ),
        (state) => {
          state.isAdImagesLoading = true;
          changeGeneratingStatusHelper(state, { genType: ID8_GEN_TYPES.AD, status: true });
        },
      )
      .addMatcher(
        isAnyOf(
          runAgainAd.rejected,
          modifyAdConcept.rejected,
          recreateAd.rejected,

          recreateAdConsumer.rejected,
          runAgainAdConsumer.rejected,
          modifyAdConceptConsumer.rejected,
        ),
        (state) => {
          state.isAdImagesLoading = false;
          changeGeneratingStatusHelper(state, { genType: ID8_GEN_TYPES.AD, status: false });
        },
      )

      //IMAGES_LOADERS
      .addMatcher(
        isAnyOf(
          runAgain.fulfilled,
          modifyConcept.fulfilled,
          customImage.fulfilled,
          consumerRunAgain.fulfilled,
          consumerModifyConcept.fulfilled,
          consumerCustomImage.fulfilled,
        ),
        (state) => {
          state.isImagesLoading = false;
          changeGeneratingStatusHelper(state, { genType: ID8_GEN_TYPES.IMAGES, status: false });
        },
      )
      .addMatcher(
        isAnyOf(
          runAgain.pending,
          modifyConcept.pending,
          customImage.pending,
          consumerRunAgain.pending,
          consumerModifyConcept.pending,
          consumerCustomImage.pending,
        ),
        (state) => {
          state.isImagesLoading = true;
          changeGeneratingStatusHelper(state, { genType: ID8_GEN_TYPES.IMAGES, status: true });
        },
      )
      .addMatcher(
        isAnyOf(
          runAgain.rejected,
          modifyConcept.rejected,
          customImage.rejected,
          consumerRunAgain.rejected,
          consumerModifyConcept.rejected,
          consumerCustomImage.rejected,
        ),
        (state) => {
          state.isImagesLoading = false;
          changeGeneratingStatusHelper(state, { genType: ID8_GEN_TYPES.IMAGES, status: false });
        },
      )

      .addMatcher(
        (action) => action.type.endsWith('/pending'),
        (state, action) => {
          const actionType = action.type.replace('/pending', '');
          state.loadingActions[actionType] = true;
        },
      )
      .addMatcher(
        (action) => action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected'),
        (state, action) => {
          const actionType = action.type.replace(/\/(fulfilled|rejected)$/, '');
          state.loadingActions[actionType] = false;
        },
      );
  },
});

export const {
  changeId8Tab,

  changeLocalMessage,

  setIsDirty,
  setEditableMessage,
  changeEditableMessage,

  likeLocalMessage,

  changeTableSort,
  changeTableSearch,

  changeGeneratingStatus,

  resetId8MessageLogs,
  resetId8Params,

  changeImagesTable,
  shiftPromptToImages,
  changeGeneratingStatusForPrompt,

  changeLikedImagesTable,

  changeAdImagesTable,

  resetId8Agents,

  changeActiveGenStatus,
} = id8Slice.actions;

export default id8Slice.reducer;
